/**
 * API client wrapper with authentication
 * Handles all HTTP requests to the backend API
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

/**
 * Build an absolute API URL for browser-loaded resources (`<img src>`, `<a href>`,
 * window.open) that can't go through {@link apiClient}'s fetch wrapper.
 *
 * These MUST be prefixed with the API base. A bare `/api/...` path only works in
 * dev because Vite proxies `/api` to the backend — in prod the frontend is a
 * static origin (e.g. app.sfnewman.com) with no `/api` rewrite, so a relative
 * path 404s against the static origin instead of hitting VITE_API_URL.
 *
 * `path` follows the same convention as apiClient endpoints (no `/api` prefix),
 * e.g. `apiUrl('/expenses/123/receipt')`.
 */
export function apiUrl(path: string): string {
  return `${API_BASE_URL}${path}`;
}

interface FetchOptions extends RequestInit {
  params?: Record<string, string | number | boolean | undefined>;
}

// Custom error class that includes the full API response
export class APIError extends Error {
  public readonly status: number;
  public readonly code: string | undefined;
  public readonly data: Record<string, unknown>;

  constructor(message: string, status: number, data: Record<string, unknown>) {
    super(message);
    this.name = 'APIError';
    this.status = status;
    this.code = typeof data.code === 'string' ? data.code : typeof data.error === 'string' ? data.error : undefined;
    this.data = data;
  }
}

class APIClient {
  private baseURL: string;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
  }

  /**
   * Make an authenticated request to the API
   */
  private async request<T>(
    endpoint: string,
    options: FetchOptions = {}
  ): Promise<T> {
    const { params, ...fetchOptions } = options;

    // Build URL with query params
    let url = `${this.baseURL}${endpoint}`;
    if (params) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
      const queryString = searchParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }

    // Make request with credentials (cookies)
    const response = await fetch(url, {
      ...fetchOptions,
      credentials: 'include', // Include session cookies
      headers: {
        'Content-Type': 'application/json',
        ...fetchOptions.headers,
      },
    });

    // Handle non-JSON responses (like file downloads)
    const contentType = response.headers.get('content-type');
    if (contentType && !contentType.includes('application/json')) {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response as unknown as T;
    }

    // Parse JSON response
    const data = await response.json();

    if (!response.ok) {
      throw new APIError(
        data.error || data.message || `HTTP error! status: ${response.status}`,
        response.status,
        data
      );
    }

    return data;
  }

  /**
   * GET request
   */
  async get<T>(endpoint: string, options?: FetchOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  /**
   * POST request
   */
  async post<T>(endpoint: string, body?: unknown, options?: FetchOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: body ? JSON.stringify(body) : null,
    });
  }

  /**
   * PUT request
   */
  async put<T>(endpoint: string, body?: unknown, options?: FetchOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: body ? JSON.stringify(body) : null,
    });
  }

  /**
   * PATCH request
   */
  async patch<T>(endpoint: string, body?: unknown, options?: FetchOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: body ? JSON.stringify(body) : null,
    });
  }

  /**
   * DELETE request
   */
  async delete<T>(endpoint: string, body?: unknown, options?: FetchOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'DELETE',
      body: body ? JSON.stringify(body) : null,
    });
  }
}

// Export singleton instance
export const apiClient = new APIClient(API_BASE_URL);
